import React, {useEffect, useState} from 'react';
import {useParams,Link} from 'react-router-dom';
import {
    Box,
    Text,
    Image,
    VStack,
    HStack,
    Center,
    Spinner,
    ButtonGroup,
    ButtonProps,
    Card,
    CardHeader,
    CardBody,
    Flex,
    GridItem,
    Grid,
    Button,
    type GridItemProps,
    useToast,
    IconButton,
    Select,
} from '@chakra-ui/react';
import {FaHeart, FaComment, FaEye, FaShare, FaTrash, FaEdit} from 'react-icons/fa';
import {customAuthAxios} from '../../../utils/customAuthAxios';
import {getDjangoToken} from '../../../utils/instagram/services/apiService';
import {useNavigate} from 'react-router-dom';

interface InstagramDmContent {
    id: number;
    name: string;
    type: string;
    valid_period?: {
        start_at: string;
        end_at: string;
    };
}

interface InstagramPackage {
    id: number;
    package_name: string;
    dm_contents?: number[]; // ← optional にする
    created_at: string;
}

interface InstagramBotSet {
    id: number;
    name: string;
    subject: string;
    target_posts: string[];
    engagement: string[];
    dm_contents: number[];
    created_at: string;
    updated_at: string;
}

export const TableHeaderCell = (props: GridItemProps) => {
    return (
        <GridItem
            bg="gray.200"
            textAlign="left"
            py={2}
            fontWeight="bold"
            paddingLeft="10px"
            {...props}
        />
    );
};

const InstagramBotSettings: React.FC = () => {
    const {restaurantId, contentId} = useParams<{ restaurantId: string; contentId: string }>();
    const [dmContents, setDmContents] = useState<InstagramDmContent[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [packages, setPackages] = useState<InstagramPackage[]>([]);
    const [isLoadingPackages, setIsLoadingPackages] = useState<boolean>(true);
    const [botSets, setBotSets] = useState<InstagramBotSet[]>([]);
    const toast = useToast();
    const navigate = useNavigate();

    const fetchDmContents = async () => {
        try {
            setIsLoading(true);
            const djangoToken = await getDjangoToken();
            const response = await customAuthAxios.get(
                `/api/restaurants/${restaurantId}/instagram/dm/list_for_bot/`,
                {
                    headers: {Authorization: `Bearer ${djangoToken}`},
                }
            );
            console.log('DM Contents Response:', response.data);
            setDmContents(response.data);
        } catch (error) {
            console.error('Failed to fetch DM contents:', error);
            toast({
                title: 'エラー',
                description: 'DMコンテンツの取得に失敗しました',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDmContents();
    }, [restaurantId]);

    const handleDelete = async (contentId: number) => {
        if (!window.confirm('このDMコンテンツを削除してもよろしいですか？')) {
            return;
        }

        try {
            const djangoToken = await getDjangoToken();
            await customAuthAxios.delete(
                `/api/restaurants/${restaurantId}/instagram/dm/${contentId}/`,
                {
                    headers: {Authorization: `Bearer ${djangoToken}`},
                }
            );
            toast({
                title: '成功',
                description: 'DMコンテンツを削除しました',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            // fetchDmContents(); // 一覧を再取得
            setDmContents((prev) => prev.filter(content => content.id !== contentId));
        } catch (error) {
            console.error('Failed to delete DM content:', error);
            toast({
                title: 'エラー',
                description: 'DMコンテンツの削除に失敗しました',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const fetchPackages = async () => {
        try {
            setIsLoadingPackages(true);
            const djangoToken = await getDjangoToken();
            const response = await customAuthAxios.get(
                `/api/restaurants/${restaurantId}/instagram/packages/`,
                {
                    headers: { Authorization: `Bearer ${djangoToken}` },
                }
            );

            console.log('Packages Response:', response.data);
            setPackages(response.data);
        } catch (error) {
            console.error('Failed to fetch packages:', error);
            toast({
                title: 'エラー',
                description: 'パッケージの取得に失敗しました',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoadingPackages(false);
        }
    };

    const handleDeletePackage = async (packageId: number) => {
        if (!window.confirm('このパッケージを削除してもよろしいですか？')) {
            return;
        }

        try {
            const djangoToken = await getDjangoToken();
            await customAuthAxios.delete(
                `/api/restaurants/${restaurantId}/instagram/packages/${packageId}/`,
                {
                    headers: { Authorization: `Bearer ${djangoToken}` },
                }
            );
            toast({
                title: '成功',
                description: 'パッケージを削除しました',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            // fetchPackages();
            setPackages((prev) => prev.filter(pkg => pkg.id !== packageId));
        } catch (error) {
            console.error('Failed to delete package:', error);
            toast({
                title: 'エラー',
                description: 'パッケージの削除に失敗しました',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    useEffect(() => {
        if (restaurantId) {
            fetchPackages();
        }
    }, [restaurantId]);

    const fetchBotSets = async () => {
        try {
            setIsLoading(true);
            const djangoToken = await getDjangoToken();
            const response = await customAuthAxios.get(
                `/api/restaurants/${restaurantId}/instagram/bot/sets/`,
                {
                    headers: {Authorization: `Bearer ${djangoToken}`},
                }
            );
            console.log('Bot Sets Response:', response.data);
            setBotSets(response.data);
        } catch (error) {
            console.error('Failed to fetch bot sets:', error);
            toast({
                title: 'エラー',
                description: 'Botセットの取得に失敗しました',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteBotSet = async (botSetId: number) => {
        if (!window.confirm('このBotセットを削除してもよろしいですか？')) {
            return;
        }

        try {
            const djangoToken = await getDjangoToken();
            await customAuthAxios.delete(
                `/api/restaurants/${restaurantId}/instagram/bot/sets/${botSetId}/`,
                {
                    headers: {Authorization: `Bearer ${djangoToken}`},
                }
            );
            toast({
                title: '成功',
                description: 'Botセットを削除しました',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            // fetchBotSets();
            setBotSets((prev) => prev.filter(set => set.id !== botSetId));
        } catch (error) {
            console.error('Failed to delete bot set:', error);
            toast({
                title: 'エラー',
                description: 'Botセットの削除に失敗しました',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    useEffect(() => {
        fetchBotSets();
    }, [restaurantId]);

    return (
        <Box p={4} width="100%">
            <Card marginY={"20px"}>
                <CardHeader>
                    <Flex justify="space-between">
                        <Text fontWeight="bold" fontSize="lg">Botセット</Text>
                        <Button colorScheme="blue" onClick={() => navigate('../instagram/bot/create')}>
                            新規作成
                        </Button>
                    </Flex>
                </CardHeader>

                <CardBody>
                    <Grid templateColumns="repeat(4,1fr) 80px" gap={2} backgroundColor="gray.100" w="100%" h="40px">
                        <TableHeaderCell>セット名</TableHeaderCell>
                        <TableHeaderCell>対象投稿</TableHeaderCell>
                        <TableHeaderCell>エンゲージメント</TableHeaderCell>
                        <TableHeaderCell>選択中のコンテンツ</TableHeaderCell>
                        <TableHeaderCell></TableHeaderCell>
                    </Grid>

                    {isLoading ? (
                        <Center py={4}>
                            <Spinner  />
                        </Center>
                    ) : (
                        botSets.map((botSet) => (
                            <Grid
                                key={botSet.id}
                                templateColumns="repeat(4,1fr) 80px"
                                gap={2}
                                w="100%"
                                minH="40px"
                                alignItems="center"
                                _hover={{ bg: "gray.50" }}
                            >
                                <GridItem pl={2} overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">{botSet.name}</GridItem>
                                <GridItem pl={2} overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
                                    {typeof botSet.subject === 'object' && botSet.subject !== null
                                        ? ((botSet.subject as any).text || JSON.stringify(botSet.subject))
                                        : botSet.subject}
                                </GridItem>

                                <GridItem pl={2} overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">{botSet.engagement.join(', ')}</GridItem>
                                <GridItem pl={2} overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
                                    {Array.isArray(botSet.target_posts) && botSet.target_posts.length > 0 ? (
                                        <Flex>
                                            {botSet.target_posts.map((postId, index) => (
                                                <Box key={index} mr={2}>
                                                    <Text>
                                                        {/* 単にIDを表示するだけにする */}
                                                        投稿ID: {postId}
                                                    </Text>
                                                </Box>
                                            ))}
                                        </Flex>
                                    ) : Array.isArray(botSet.target_posts) && botSet.target_posts.includes('all') ? (
                                        '全ての投稿'
                                    ) : (
                                        '0個のコンテンツ'
                                    )}
                                </GridItem>



                                <GridItem>
                                    <HStack spacing={2} justify="flex-end" pr={2}>
                                        <IconButton
                                            aria-label="編集"
                                            icon={<FaEdit size="20px" />}
                                            size="sm"
                                            colorScheme="blue"
                                            onClick={() => navigate(`/restaurants/${restaurantId}/instagram/bot/create?edit=${botSet.id}`)}
                                        />
                                        <IconButton
                                            aria-label="削除"
                                            icon={<FaTrash size="20px" />}
                                            size="sm"
                                            colorScheme="red"
                                            onClick={() => handleDeleteBotSet(botSet.id)}
                                        />
                                    </HStack>
                                </GridItem>
                            </Grid>
                        ))
                    )}
                </CardBody>
            </Card>

            <Card marginY={"20px"}>
                <Grid templateColumns="repeat(2,1fr)" gap={4}>
                    <GridItem>
                        <Card>
                            <CardHeader>
                                <Flex justify="space-between">
                                    <Text fontWeight={"bold"}>DMコンテンツ</Text>
                                    <Button colorScheme="blue" onClick={() => navigate('../instagram/dm/create')}>
                                        新規作成
                                    </Button>
                                </Flex>
                            </CardHeader>
                            <CardBody>
                                <Grid templateColumns="repeat(3,1fr) 80px" gap={2} backgroundColor="gray.100" w="100%" h="40px">
                                    <TableHeaderCell>コンテンツ名</TableHeaderCell>
                                    <TableHeaderCell>タイプ</TableHeaderCell>
                                    <TableHeaderCell>有効期限</TableHeaderCell>
                                    <TableHeaderCell></TableHeaderCell>
                                </Grid>
                                {isLoading ? (
                                    <Center py={4}>
                                        <Spinner  />
                                    </Center>
                                ) : dmContents.length > 0 ? (
                                    dmContents.map((content) => (
                                        <Grid key={content.id} templateColumns="repeat(3,1fr) 80px" gap={2} w="100%" py={2}>
                                            <GridItem overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">{content.name}</GridItem>
                                            <GridItem overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">{content.type}</GridItem>
                                            <GridItem overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
                                                {content.valid_period ? (
                                                    `${formatDate(content.valid_period.start_at)} - ${formatDate(content.valid_period.end_at)}`
                                                ) : (
                                                    '期限なし'
                                                )}
                                            </GridItem>
                                            <GridItem>
                                                <HStack spacing={2}>
                                                    <IconButton
                                                        aria-label="編集"
                                                        icon={<FaEdit size="20px" />}
                                                        size="sm"
                                                        colorScheme="blue"
                                                        onClick={() => navigate(`/restaurants/${restaurantId}/instagram/dm/create?edit=${content.id}`)}
                                                    />
                                                    <IconButton
                                                        aria-label="削除"
                                                        icon={<FaTrash size="20px" />}
                                                        size="sm"
                                                        colorScheme="red"
                                                        onClick={() => handleDelete(content.id)}
                                                    />
                                                </HStack>
                                            </GridItem>
                                        </Grid>
                                    ))
                                ) : (
                                    <Center py={4}>
                                        <Text>DMコンテンツがありません</Text>
                                    </Center>
                                )}
                            </CardBody>
                        </Card>
                    </GridItem>
                    <GridItem>
                        <Card>
                            <CardHeader>
                                <Flex justify="space-between">
                                    <Text fontWeight={"bold"}>パッケージ</Text>
                                    <Button colorScheme="blue" onClick={() => navigate('../instagram/package/create')}>
                                        新規作成
                                    </Button>
                                </Flex>
                            </CardHeader>
                            <CardBody>
                                <Grid templateColumns="repeat(3,1fr) 80px" gap={2} backgroundColor="gray.100" w="100%" h="40px">
                                    <TableHeaderCell>パッケージ名</TableHeaderCell>
                                    <TableHeaderCell>コンテンツ数</TableHeaderCell>
                                    <TableHeaderCell>作成日</TableHeaderCell>
                                    <TableHeaderCell></TableHeaderCell>
                                </Grid>
                                {isLoadingPackages ? (
                                    <Center py={4}>
                                        <Spinner  />
                                    </Center>
                                ) : (
                                    packages.map((pkg) => (
                                        <Grid key={pkg.id} templateColumns="repeat(3,1fr) 80px" gap={2} w="100%" py={2}>
                                            <GridItem overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">{pkg.package_name}</GridItem>
                                            <GridItem>{Array.isArray(pkg.dm_contents) ? pkg.dm_contents.length : 0}</GridItem>
                                            <GridItem>{formatDate(pkg.created_at)}</GridItem>
                                            <GridItem>
                                                <HStack spacing={2}>
                                                    <IconButton
                                                        aria-label="編集"
                                                        icon={<FaEdit size="20px" />}
                                                        size="sm"
                                                        colorScheme="blue"
                                                        onClick={() => navigate(`/restaurants/${restaurantId}/instagram/package/create?edit=${pkg.id}`)}
                                                    />
                                                    <IconButton
                                                        aria-label="削除"
                                                        icon={<FaTrash size="20px" />}
                                                        size="sm"
                                                        colorScheme="red"
                                                        onClick={() => handleDeletePackage(pkg.id)}
                                                    />
                                                </HStack>
                                            </GridItem>
                                        </Grid>
                                    ))
                                )}
                            </CardBody>
                        </Card>
                    </GridItem>
                </Grid>
            </Card>

            <Card marginY={"20px"}>
                <CardHeader>
                    <Flex justify="space-between">
                        <Text fontWeight="bold" fontSize="lg">新着DM</Text>
                        <Button as="a" href="../dm/receive" rel="noopener noreferrer" colorScheme="blue">送信一覧</Button>
                    </Flex>
                </CardHeader>

                <CardBody>
                    <Grid templateColumns="repeat(3,1fr)" gap={2} backgroundColor="gray.100" w="100%" h="40px">
                        <TableHeaderCell>ユーザー名</TableHeaderCell>
                        <TableHeaderCell>内容</TableHeaderCell>
                        <TableHeaderCell>受信日時</TableHeaderCell>
                    </Grid>
                </CardBody>
            </Card>
        </Box>
    );
};

export default InstagramBotSettings;
